/**
 * Mobile "best on desktop" gate for views that are genuinely desktop-shaped:
 * the kanban board (touch drag-and-drop needs a different library), the
 * timesheet 9-row × 7-column grid, and HR matrices.
 *
 * On phone-width viewports we render a polite notice instead of trying to
 * cram the view into 375px. Everywhere else (tablet + desktop) the children
 * render unchanged.
 */

import type { CSSProperties, ReactNode } from "react";

import { useIsMobile } from "@/hooks/useIsMobile";

export function DesktopOnly({
  children,
  title,
  blurb,
  breakpoint,
}: {
  children: ReactNode;
  /** Short label for the view, used in the prompt heading. */
  title: string;
  /** One-sentence explanation of why this view needs more room. */
  blurb: string;
  /** Override the default 1024px breakpoint if a view is fine on tablet. */
  breakpoint?: number;
}) {
  const isMobile = useIsMobile(breakpoint ?? 1024);
  if (!isMobile) return <>{children}</>;
  return <BestOnDesktopNotice title={title} blurb={blurb} />;
}

function BestOnDesktopNotice({ title, blurb }: { title: string; blurb: string }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  return (
    <div style={wrap}>
      <div style={iconBox} aria-hidden>
        {/* Two-monitor-ish glyph */}
        <span style={iconGlyph}>▤</span>
      </div>
      <h1 style={heading}>{title} is best on desktop</h1>
      <p style={prose}>{blurb}</p>
      <p style={proseMute}>
        Open <strong style={{ color: "var(--ink)" }}>{url}</strong> on a laptop or
        desktop. Other minute2sec views (login, settings, wiki, my progress, members)
        work on mobile too.
      </p>
      <button
        type="button"
        onClick={() => {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(url).catch(() => undefined);
          }
        }}
        style={copyBtn}
      >
        Copy this URL
      </button>
    </div>
  );
}

const wrap: CSSProperties = {
  maxWidth: 480,
  margin: "32px auto",
  padding: 24,
  textAlign: "center",
};

const iconBox: CSSProperties = {
  width: 64,
  height: 64,
  margin: "0 auto 16px",
  borderRadius: 12,
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const iconGlyph: CSSProperties = {
  fontSize: 28,
  color: "var(--body-mid)",
  lineHeight: 1,
};

const heading: CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
  letterSpacing: "-0.2px",
  margin: "0 0 12px",
  color: "var(--ink)",
};

const prose: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "var(--body)",
  margin: "0 0 12px",
};

const proseMute: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.6,
  color: "var(--mute)",
  margin: "0 0 20px",
  wordBreak: "break-all",
};

const copyBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "10px 18px",
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
};
